/**
 * HTTP API between any client and the Digester server (specs/api.md has the prose and the limits).
 *
 * Conventions
 *  - JSON in, JSON out, UTF-8. Errors: HTTP status + body `ApiErrorBody`.
 *  - Auth, one of:
 *      session  — cookie `digester_session` (web) or `Authorization: Bearer <session token>` (apps)
 *      install  — `X-Digester-Install: <install token>` (account-less Free installs; relay + IMAP only)
 *      device   — session + `X-Digester-Device: <DeviceId>` (sync endpoints)
 *      connector— `Authorization: Bearer dgc_…` or the secret path (MCP only)
 *      push     — the token in the URL path (push ingest only)
 *  - Every mutating browser request carries `X-Digester-Client: web` (CSRF guard; api.md).
 *
 * `API` lists every route. Request/response types are named <Route>Request / <Route>Response.
 */

import type { BillingPeriod, BillingProviderId, CheckoutSession, Entitlement, PlanOffer, Usage } from './billing.js';
import type { Connector, ConnectorAccess } from './entities.js';
import type { DigesterErrorShape } from './errors.js';
import type { ConnectorId, DeviceId, EpochMs, SourceId } from './ids.js';
import type { ScoreRequest, ScoreResponse } from './scoring.js';
import type { PushItem } from './sources.js';
import type { DeviceInfo, PullResponse, PushRequest, PushResponse } from './sync.js';

export const API = {
  health: 'GET /api/health',
  // auth
  signIn: 'POST /api/auth/sign-in',
  requestCode: 'POST /api/auth/request-code',
  verifyCode: 'POST /api/auth/verify-code',
  setPassword: 'POST /api/auth/password',
  logout: 'POST /api/auth/logout',
  me: 'GET /api/auth/me',
  install: 'POST /api/install',
  // devices + sync
  devicesList: 'GET /api/devices',
  devicesRegister: 'POST /api/devices',
  devicesRevoke: 'DELETE /api/devices/:id',
  syncPush: 'POST /api/sync/push',
  syncPull: 'GET /api/sync/pull',
  syncReset: 'POST /api/sync/reset',
  // device services
  relay: 'POST /api/relay',
  imapMailboxes: 'POST /api/imap/mailboxes',
  imapFetch: 'POST /api/imap/fetch',
  score: 'POST /api/score',
  // push ingest
  pushTokenCreate: 'POST /api/push-tokens',
  pushTokenRevoke: 'DELETE /api/push-tokens/:sourceId',
  pushIngest: 'POST /api/push/:token',
  // connectors (MCP tokens)
  connectorsList: 'GET /api/connectors',
  connectorsCreate: 'POST /api/connectors',
  connectorsUpdate: 'PATCH /api/connectors/:id',
  connectorsRotate: 'POST /api/connectors/:id/rotate',
  connectorsDelete: 'DELETE /api/connectors/:id',
  // billing
  billingOffers: 'GET /api/billing/offers',
  billingEntitlement: 'GET /api/billing/entitlement',
  billingCheckout: 'POST /api/billing/checkout',
  billingMockConfirm: 'POST /api/billing/mock/confirm',
  billingCancel: 'POST /api/billing/cancel',
  billingWebhook: 'POST /api/billing/webhook/:provider',
  // account
  account: 'GET /api/account',
  accountExport: 'GET /api/account/export',
  accountDeleteRequest: 'POST /api/account/delete/request',
  accountDeleteConfirm: 'POST /api/account/delete/confirm',
  // MCP
  mcp: 'POST /mcp',
  mcpSecretPath: 'POST /mcp/t/:secret',
} as const;

export type ApiRoute = keyof typeof API;

/** Error body for every non-2xx response. HTTP status mapping is in api.md "Errors". */
export interface ApiErrorBody {
  error: Omit<DigesterErrorShape, 'name'>;
}

// ── Auth ───────────────────────────────────────────────────────────────────────────────────────

/**
 * POST /api/auth/sign-in — the normal sign-in. Answers (anti-enumeration: an unknown address, an
 * account without a password and a wrong password are the same answer):
 *  - 200 `SignInResponse` + the session cookie;
 *  - 401 `unauthenticated` "The e-mail address or the password is not right." — wrong, unknown or no password;
 *  - 429 `rate_limited` with `details.reason = 'password_paused'` and `details.retryAfterMs` — too many
 *    wrong passwords in a row for this address: only a code signs in until the pause ends.
 */
export interface SignInRequest {
  email: string;
  password: string;
}
export type SignInResponse = VerifyCodeResponse;

/** `details.reason` of the 429 answer while password sign-in for an address is paused. */
export const PASSWORD_PAUSED = 'password_paused';

export interface RequestCodeRequest {
  email: string;
}
/** Always `{ ok: true }`, whether or not a code was sent (anti-enumeration). */
export interface OkResponse {
  ok: true;
}

export interface VerifyCodeRequest {
  email: string;
  /** Six digits. */
  code: string;
}
export interface VerifyCodeResponse {
  /** Session token for `Authorization: Bearer`. Also set as the httpOnly cookie. */
  token: string;
  account: AccountSummary;
}

export interface AccountSummary {
  email: string;
  entitlement: Entitlement;
  createdAt: EpochMs;
  /** True once the account has a password (it then signs in with it; codes only for special cases). */
  hasPassword: boolean;
}

/**
 * POST /api/auth/password (session) — set or change the password. `currentPassword` is required when
 * the account has a password, unless this session was signed in with a code in the last
 * `password.reset_window_minutes` (the "forgot password" path); when given, it is always checked.
 * Ends every OTHER session of the account; this session, devices' sync registrations and connector
 * tokens are kept.
 */
export interface SetPasswordRequest {
  password: string;
  currentPassword?: string;
}
export interface SetPasswordResponse {
  account: AccountSummary;
  /** How many other sessions (other browsers and apps) were signed out. */
  endedSessions: number;
}

/** GET /api/auth/me */
export type MeResponse = AccountSummary;

/** POST /api/install — no auth, rate-limited per IP. */
export interface InstallResponse {
  /** 'dgi_…' — store in local_settings.installToken; send as X-Digester-Install. */
  token: string;
}

// ── Devices + sync ─────────────────────────────────────────────────────────────────────────────

export interface DevicesResponse {
  devices: DeviceInfo[];
  max: number;
}
export interface RegisterDeviceRequest {
  id: DeviceId;
  name: string;
  platform: DeviceInfo['platform'];
}

export type SyncPushRequest = PushRequest;
export type SyncPushResponse = PushResponse;
/** GET /api/sync/pull?since=<n>&limit=<n> */
export type SyncPullResponse = PullResponse;
/** POST /api/sync/reset — delete the server replica (the next push from any device rebuilds it). */
export interface SyncResetRequest {
  confirm: 'delete-replica';
}

// ── Relay ──────────────────────────────────────────────────────────────────────────────────────

export interface RelayRequest {
  url: string;
  method: 'GET' | 'POST';
  /** Only Accept, Accept-Language, If-None-Match, If-Modified-Since, Content-Type are forwarded. */
  headers?: Record<string, string>;
  /** POST only, and only for targets on the relay's POST allow-list (api.md "Relay"). */
  body?: string;
}

/**
 * 200 for every completed upstream exchange — the upstream status is in `status`.
 * The relay itself fails with fetch_blocked (422), rate_limited (429), plan_limit (402),
 * fetch_failed (502/504).
 */
export interface RelayResponse {
  status: number;
  /** content-type, etag, last-modified, retry-after, location (lower-cased); nothing else. */
  headers: Record<string, string>;
  /** Text body decoded with the upstream charset. Binary content types are refused. */
  text: string;
  /** Final URL after redirects. */
  url: string;
}

// ── IMAP bridge ────────────────────────────────────────────────────────────────────────────────

export interface ImapAccountDto {
  host: string;
  port: number;
  security: 'tls' | 'starttls' | 'none';
  user: string;
  /** Used for this one request, held in memory only, never logged or stored. */
  password: string;
}

export interface ImapMailboxesRequest {
  account: ImapAccountDto;
}
export interface ImapMailboxesResponse {
  mailboxes: string[];
}

export interface ImapFetchRequest {
  account: ImapAccountDto;
  mailbox: string;
  /** Return messages with uid > sinceUid, ascending. 0 = the newest `limit` messages. */
  sinceUid: number;
  /** The uidValidity the client last saw; a mismatch makes the server answer from uid 0 again. */
  uidValidity: number | null;
  /** 1..imap.max_messages_per_fetch. */
  limit: number;
}
export interface ImapFetchResponse {
  uidValidity: number;
  messages: Array<{
    uid: number;
    internalDate: EpochMs;
    /** Base64 of the RFC 822 source, cut at imap.max_message_bytes. */
    rfc822: string;
    truncated: boolean;
  }>;
  more: boolean;
}

// ── Scoring proxy ──────────────────────────────────────────────────────────────────────────────

export type ScoreApiRequest = ScoreRequest;
export type ScoreApiResponse = ScoreResponse;

// ── Push ingest ────────────────────────────────────────────────────────────────────────────────

export interface PushTokenCreateRequest {
  sourceId: SourceId;
}
export interface PushTokenCreateResponse {
  /** Full ingest URL containing the token; shown once. */
  url: string;
  /** Last four characters, for display. */
  hint: string;
}

/** POST /api/push/:token — the token is the only credential. */
export interface PushIngestRequest {
  items: PushItem[];
}
export interface PushIngestResponse {
  inserted: number;
  duplicates: number;
  skipped: number;
  overLimit: number;
}

// ── Connectors ─────────────────────────────────────────────────────────────────────────────────

export interface ConnectorsResponse {
  connectors: Connector[];
  max: number;
  requestsToday: number;
  requestsPerDay: number;
  /** Base MCP endpoint, e.g. 'https://api.digester.news/mcp'. */
  endpoint: string;
}

export interface ConnectorCreateRequest {
  name: string;
  access: ConnectorAccess;
}
/** The secrets are returned exactly once. */
export interface ConnectorSecretsResponse {
  connector: Connector;
  /** 'dgc_…' for `Authorization: Bearer`. */
  token: string;
  /** Full URL with the secret path, for clients that cannot send headers. */
  secretUrl: string;
}
export interface ConnectorUpdateRequest {
  name?: string;
  access?: ConnectorAccess;
  enabled?: boolean;
}
export interface ConnectorIdParam {
  id: ConnectorId;
}

// ── Billing ────────────────────────────────────────────────────────────────────────────────────

export interface BillingOffersResponse {
  offers: PlanOffer[];
  provider: BillingProviderId;
}
export interface BillingEntitlementResponse {
  entitlement: Entitlement;
}
export interface BillingCheckoutRequest {
  multiplier: number;
  period: BillingPeriod;
  returnUrl: string;
}
export type BillingCheckoutResponse = CheckoutSession;
/** Mock provider only (404 when another provider is configured). */
export interface BillingMockConfirmRequest {
  reference: string;
}

// ── Account ────────────────────────────────────────────────────────────────────────────────────

export interface AccountResponse {
  account: AccountSummary;
  /** Server-side usage counters for today plus replica size. */
  usage: Pick<Usage, 'syncDevices' | 'connectors' | 'connectorRequestsToday' | 'scoredToday'> & {
    relayRequestsToday: number;
    replicaBytes: number;
  };
}

/** POST /api/account/delete/request — emails a one-time code to the account address. */
export type AccountDeleteRequestResponse = OkResponse;
export interface AccountDeleteConfirmRequest {
  code: string;
}

// ── Health ─────────────────────────────────────────────────────────────────────────────────────

export interface HealthResponse {
  ok: true;
  version: string;
  schemaVersion: number;
}
